import React, { useMemo, useState } from 'react';
import { ScrollTextIcon, MapPinIcon, DollarSignIcon, ShoppingCartIcon, LogInIcon, LogOutIcon, SearchIcon, FilterIcon, CalendarIcon, UserIcon, NavigationIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../context/AuthContext';
import { ActivityLog } from '../types';
function getActionIcon(action: string) {
  const a = action.toLowerCase();
  if (a.includes('visita')) return <MapPinIcon className="w-4 h-4" data-id="element-364" data-mp="1ht2ykguxcjq6" />;
  if (a.includes('cobro') || a.includes('pago')) return <DollarSignIcon className="w-4 h-4" data-id="element-365" data-mp="1cu9icgo96emb" />;
  if (a.includes('venta') || a.includes('pedido')) return <ShoppingCartIcon className="w-4 h-4" data-id="element-366" data-mp="17t2c4m1rdnhr" />;
  if (a.includes('inicio de sesión')) return <LogInIcon className="w-4 h-4" data-id="element-367" data-mp="12ur0q6vtf4xf" />;
  if (a.includes('cierre de sesión')) return <LogOutIcon className="w-4 h-4" data-id="element-368" data-mp="30ukyf6cx8184" />;
  if (a.includes('factura') || a.includes('nota de crédito') || a.includes('nc')) return <DollarSignIcon className="w-4 h-4" data-id="element-369" data-mp="35lt7g1ac9kkr" />;
  if (a.includes('producto') || a.includes('inventario')) return <ShoppingCartIcon className="w-4 h-4" data-id="element-370" data-mp="2qxon6svn10zv" />;
  if (a.includes('planificación') || a.includes('televentas')) return <ClockIcon className="w-4 h-4" data-id="element-371" data-mp="2vvhvbrgi452s" />;
  if (a.includes('usuario') || a.includes('rol') || a.includes('comision')) return <UserIcon className="w-4 h-4" data-id="element-372" data-mp="3jiy79jjgln7k" />;
  if (a.includes('farmacia') || a.includes('caja vacía')) return <MapPinIcon className="w-4 h-4" data-id="element-373" data-mp="39mmwwz3fj7fr" />;
  return <ScrollTextIcon className="w-4 h-4" data-id="element-374" data-mp="39lla5xd1wwi7" />;
}
function getActionColor(action: string) {
  const a = action.toLowerCase();
  if (a.includes('visita') && (a.includes('inicio') || a.includes('inicio'))) return {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  };
  if (a.includes('visita') && a.includes('fin')) return {
    bg: 'bg-emerald-100',
    text: 'text-emerald-600',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  };
  if (a.includes('visita')) return {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  };
  if (a.includes('cobro')) return {
    bg: 'bg-emerald-100',
    text: 'text-emerald-600',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  };
  if (a.includes('venta')) return {
    bg: 'bg-blue-100',
    text: 'text-blue-600',
    dot: 'bg-blue-500',
    badge: 'bg-blue-50 text-blue-700 border-blue-200'
  };
  if (a.includes('inicio de sesión')) return {
    bg: 'bg-violet-100',
    text: 'text-violet-600',
    dot: 'bg-violet-500',
    badge: 'bg-violet-50 text-violet-700 border-violet-200'
  };
  if (a.includes('cierre de sesión')) return {
    bg: 'bg-rose-100',
    text: 'text-rose-600',
    dot: 'bg-rose-500',
    badge: 'bg-rose-50 text-rose-700 border-rose-200'
  };
  return {
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    dot: 'bg-slate-400',
    badge: 'bg-slate-50 text-slate-700 border-slate-200'
  };
}
function formatDateTime(date: Date) {
  const d = new Date(date);
  return {
    date: d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }),
    time: d.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    })
  };
}
function groupByDate(logs: ActivityLog[]) {
  const groups: Record<string, ActivityLog[]> = {};
  logs.forEach(log => {
    const d = new Date(log.timestamp);
    const key = d.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
    if (!groups[key]) groups[key] = [];
    groups[key].push(log);
  });
  return groups;
}
export function BitacoraPage() {
  const {
    activityLogs,
    user
  } = useAuth();
  const [search, setSearch] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterCategory, setFilterCategory] = useState<'todos' | 'visitas' | 'crud' | 'sesiones'>('todos');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const isAdmin = user?.rolNombre === 'Administrador';
  const getCategoryFromAction = (action: string) => {
    const a = action.toLowerCase();
    if (a.includes('visita')) return 'visitas';
    if (a.includes('sesión') || a.includes('sesion') || a.includes('inicio de ses') || a.includes('cierre de ses')) return 'sesiones';
    return 'crud';
  };
  // Unique users for filter
  const uniqueUsers = useMemo(() => {
    const users = [...new Set(activityLogs.map(l => l.userName))];
    return users.sort();
  }, [activityLogs]);
  // Show ALL activity logs including login/logout sessions
  const auditLogs = activityLogs;
  const filtered = useMemo(() => {
    return auditLogs.filter(log => {
      const matchSearch = !search || log.action.toLowerCase().includes(search.toLowerCase()) || log.userName.toLowerCase().includes(search.toLowerCase()) || log.details.toLowerCase().includes(search.toLowerCase());
      const matchUser = !filterUser || log.userName === filterUser;
      const matchCategory = filterCategory === 'todos' || getCategoryFromAction(log.action) === filterCategory;
      const logDate = new Date(log.timestamp).toISOString().split('T')[0];
      const matchDateFrom = !dateFrom || logDate >= dateFrom;
      const matchDateTo = !dateTo || logDate <= dateTo;
      return matchSearch && matchUser && matchCategory && matchDateFrom && matchDateTo;
    });
  }, [auditLogs, search, filterUser, filterCategory]);
  const grouped = useMemo(() => groupByDate(filtered), [filtered]);
  const today = new Date().toDateString();
  const todayCount = auditLogs.filter(l => new Date(l.timestamp).toDateString() === today).length;
  const withGps = auditLogs.filter(l => l.location).length;
  const crudCount = auditLogs.filter(l => getCategoryFromAction(l.action) === 'crud').length;
  const visitCount = auditLogs.filter(l => getCategoryFromAction(l.action) === 'visitas').length;
  const sessionCount = auditLogs.filter(l => getCategoryFromAction(l.action) === 'sesiones').length;
  const categoryTabs = [{
    id: 'todos',
    label: 'Todos',
    count: auditLogs.length
  }, {
    id: 'visitas',
    label: 'Visitas',
    count: visitCount
  }, {
    id: 'crud',
    label: 'Cambios',
    count: crudCount
  }, {
    id: 'sesiones',
    label: 'Sesiones',
    count: sessionCount
  }] as const;
  return <div className="space-y-6" data-id="element-375" data-mp="3noih2wc17f3j">
      <PageHeader title="Bitácora del Sistema" subtitle="Registro de actividades y auditoría" icon={<ScrollTextIcon className="w-6 h-6" data-id="element-377" data-mp="3sk7jd7ffra3j" />} data-id="element-376" data-mp="3npabi3tw76s8">
        <div className="flex items-center gap-2 px-4 py-2 bg-[#eef2fc] border border-[#d5e0f7] rounded-xl" data-id="element-378" data-mp="1f7pzm6zs18l">
          <ScrollTextIcon className="w-4 h-4 text-[#2952C4]" data-id="element-379" data-mp="67r36i1roucv" />
          <span className="text-sm font-bold text-[#1E3D96]" data-id="element-380" data-mp="b6llzjzqiiwz">
            {activityLogs.length}
          </span>
          <span className="text-sm text-[#2952C4]" data-id="element-381" data-mp="f6w39ssz8vr5">registros totales</span>
        </div>
      </PageHeader>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-stagger-1" data-id="element-382" data-mp="k7jrk6ptmyho">
        {[{
        label: 'Total registros',
        value: auditLogs.length,
        icon: <ScrollTextIcon className="w-5 h-5" data-id="element-383" data-mp="2lzofz8843dnn" />,
        color: 'text-brand-600 bg-brand-100'
      }, {
        label: 'Hoy',
        value: todayCount,
        icon: <CalendarIcon className="w-5 h-5" data-id="element-384" data-mp="wo1m89ervhc7" />,
        color: 'text-emerald-600 bg-emerald-100'
      }, {
        label: 'Cambios CRUD',
        value: crudCount,
        icon: <NavigationIcon className="w-5 h-5" data-id="element-385" data-mp="16dumie9gsa15" />,
        color: 'text-amber-600 bg-amber-100'
      }, {
        label: 'Usuarios activos',
        value: uniqueUsers.length,
        icon: <UserIcon className="w-5 h-5" data-id="element-386" data-mp="dr0e4fpx9141" />,
        color: 'text-violet-600 bg-violet-100'
      }].map(stat => <div key={stat.label} className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-4 flex items-center gap-3" data-id="element-387" data-mp="8trs241nkcxi">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${stat.color}`} data-id="element-388" data-mp="irxwoteufx62">
              {stat.icon}
            </div>
            <div data-id="element-389" data-mp="1u4gq9tcfr85c">
              <p className="text-xl font-bold text-slate-900 font-heading" data-id="element-390" data-mp="1u733a8i3ifuy">
                {stat.value}
              </p>
              <p className="text-xs text-slate-500" data-id="element-391" data-mp="26nvnsss06b7q">{stat.label}</p>
            </div>
          </div>)}
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 flex-wrap animate-stagger-2" data-id="element-392" data-mp="1spe2f1pklasz">
        {categoryTabs.map(tab => <button key={tab.id} onClick={() => setFilterCategory(tab.id)} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${filterCategory === tab.id ? 'bg-[#2952C4] text-white shadow-md' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#2952C4]/30 hover:text-[#2952C4]'}`} data-id="element-393" data-mp="1wpg4hbz7w68s">
            {tab.label}
            <span className={`ml-1.5 text-xs ${filterCategory === tab.id ? 'text-white/70' : 'text-slate-400'}`} data-id="element-394" data-mp="2fmhdd7vw6qi0">
              {tab.count}
            </span>
          </button>)}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-4 space-y-3" data-id="element-395" data-mp="tf5ama1wmh6h">
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-396" data-mp="ye8h7yxj8vgk">
          <div className="relative flex-1" data-id="element-397" data-mp="ybm8vx2tibxa">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-398" data-mp="jfo6hx2awbcm" />
            <input type="text" placeholder="Buscar por acción, usuario o detalle..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] focus:bg-white transition-all" data-id="element-399" data-mp="oere9p9o0voa" />
          </div>
          {isAdmin && <select value={filterUser} onChange={e => setFilterUser(e.target.value)} className="px-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] transition-all text-slate-700" data-id="element-400" data-mp="3nv60suomfeg3">
              <option value="" data-id="element-401" data-mp="34afnonfppu5m">Todos los usuarios</option>
              {uniqueUsers.map(u => <option key={u} value={u} data-id="element-402" data-mp="3i9oskqygx1ua">
                  {u}
                </option>)}
            </select>}
          <div className="flex items-center gap-1.5" data-id="element-403" data-mp="3dc5eps5omr2v">
            <span className="text-xs text-slate-500 font-medium" data-id="element-404" data-mp="3s61m9ou0jfp1">Desde</span>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-2.5 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] transition-all" data-id="element-405" data-mp="3n8i8eq1894xm" />
          </div>
          <div className="flex items-center gap-1.5" data-id="element-406" data-mp="esgbwafh8815">
            <span className="text-xs text-slate-500 font-medium" data-id="element-407" data-mp="9tvba9wbbmc6">Hasta</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-2.5 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] transition-all" data-id="element-408" data-mp="33lbsa22h0qse" />
          </div>
        </div>

        {(search || filterUser || dateFrom || dateTo) && <div className="flex items-center gap-2" data-id="element-409" data-mp="3mccncf0h026o">
            <span className="text-xs text-slate-500" data-id="element-410" data-mp="3mdnw6t8rw04n">
              {filtered.length} resultado(s)
            </span>
            <button onClick={() => {
          setSearch('');
          setFilterUser('');
          setDateFrom('');
          setDateTo('');
        }} className="text-xs text-[#2952C4] hover:text-[#1E3D96] font-medium" data-id="element-411" data-mp="3vc82sw126jci">
              Limpiar filtros
            </button>
          </div>}
      </div>

      {/* Activity List */}
      <div className="space-y-4 animate-stagger-3" data-id="element-412" data-mp="1ydioy0pkpn3w">
        {Object.entries(grouped).length === 0 ? <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] py-16 text-center animate-fade-in" data-id="element-413" data-mp="1tmk54r5dm6vw">
            <ScrollTextIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-414" data-mp="1ooipyhb2mrpp" />
            <p className="text-slate-500 font-medium" data-id="element-415" data-mp="1jpxoyfnzw407">No hay registros</p>
            <p className="text-sm text-slate-400 mt-1" data-id="element-416" data-mp="2hd6c0f3n9l4b">
              Prueba con otros filtros
            </p>
          </div> : Object.entries(grouped).map(([dateLabel, logs]) => <div key={dateLabel} data-id="element-417" data-mp="27dp9g6jovnck">
              <div className="flex items-center gap-3 mb-4" data-id="element-418" data-mp="27cvzkwpfohq7">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-xl" data-id="element-419" data-mp="23devo7pnnsn5">
                  <CalendarIcon className="w-3.5 h-3.5 text-slate-500" data-id="element-420" data-mp="vztje2r8xu6k" />
                  <span className="text-xs font-semibold text-slate-600 capitalize font-heading" data-id="element-421" data-mp="vwz1ahvpglgb">
                    {dateLabel}
                  </span>
                </div>
                <div className="flex-1 h-px bg-slate-100" data-id="element-422" data-mp="2ft3ezo7znhmv" />
                <span className="text-xs text-slate-400" data-id="element-423" data-mp="2klebsi7fmnb7">
                  {logs.length} eventos
                </span>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden divide-y divide-slate-50" data-id="element-424" data-mp="25y1lyhaldvf0">
                {logs.map(log => {
            const colors = getActionColor(log.action);
            const {
              date,
              time
            } = formatDateTime(log.timestamp);
            const isExpanded = expandedLog === log.id;
            return <div key={log.id} className="group" data-id="element-425" data-mp="2pmk5ai8x2els">
                      <button onClick={() => setExpandedLog(isExpanded ? null : log.id)} className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left" data-id="element-426" data-mp="2tkhv9naih4q8">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${colors.bg} ${colors.text}`} data-id="element-427" data-mp="1alk37evsat66">
                          {getActionIcon(log.action)}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-428" data-mp="1fn9dgsb6n2t7">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-429" data-mp="rwn673905mk9">
                            <span className="text-sm font-semibold text-slate-900" data-id="element-430" data-mp="ruj4mrykszn5">
                              {log.action}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${colors.badge}`} data-id="element-431" data-mp="11ni2z1jvk4jy">
                              {log.userName}
                            </span>
                          </div>
                          {log.details && <p className="text-xs text-slate-500 mt-0.5 truncate" data-id="element-432" data-mp="46swgujzce48">
                              {log.details}
                            </p>}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-433" data-mp="xufhumlgitnp">
                          {log.location && <div className="hidden sm:flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-lg" data-id="element-434" data-mp="17n4nv1y8nfbn">
                              <NavigationIcon className="w-3 h-3 text-emerald-600" data-id="element-435" data-mp="axx3hx9cvc9b" />
                              <span className="text-xs text-emerald-700 font-medium" data-id="element-436" data-mp="eypbyoaeld4j">
                                GPS
                              </span>
                            </div>}
                          <div className="text-right" data-id="element-437" data-mp="oxevsmlv17nf">
                            <p className="text-xs font-semibold text-slate-700" data-id="element-438" data-mp="1vcz13kmgz5wf">
                              {time}
                            </p>
                            <p className="text-xs text-slate-400" data-id="element-439" data-mp="20ddap73pynwk">{date}</p>
                          </div>
                          <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-440" data-mp="3kve4nebzuatm" />
                        </div>
                      </button>

                      {isExpanded && <div className="px-5 pb-4 bg-slate-50 border-t border-slate-100" data-id="element-441" data-mp="362c9jt97sba2">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4" data-id="element-442" data-mp="3244t6zinxbze">
                            <div data-id="element-443" data-mp="2x25nvk3d44zt">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-444" data-mp="2x635u2e7eqi4">
                                Usuario
                              </p>
                              <p className="text-sm text-slate-900 font-medium" data-id="element-445" data-mp="2n7njsdu1a7fh">
                                {log.userName}
                              </p>
                            </div>
                            <div data-id="element-446" data-mp="lc3blw74jv1r">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-447" data-mp="3qehmr2abd97k">
                                Fecha y Hora
                              </p>
                              <p className="text-sm text-slate-900 font-medium" data-id="element-448" data-mp="3hm607ribm5wv">
                                {date} a las {time}
                              </p>
                            </div>
                            {log.details && <div className="sm:col-span-2" data-id="element-449" data-mp="37m5e5xt2hboz">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-450" data-mp="3cncn406y4gx8">
                                  Detalles
                                </p>
                                <p className="text-sm text-slate-900" data-id="element-451" data-mp="32o3vhdzjzee1">
                                  {log.details}
                                </p>
                              </div>}
                            {log.location && <div className="sm:col-span-2" data-id="element-452" data-mp="10v665s4zj3xu">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-453" data-mp="2lg4cbmprmnff">
                                  Ubicación GPS
                                </p>
                                <div className="flex items-center gap-2" data-id="element-454" data-mp="2v5wb6t4we2pp">
                                  <NavigationIcon className="w-4 h-4 text-emerald-600" data-id="element-455" data-mp="2qdw6yohsonnb" />
                                  <span className="text-sm text-slate-900 font-mono" data-id="element-456" data-mp="35323u5t5u1s1">
                                    {log.location.lat.toFixed(6)},{' '}
                                    {log.location.lng.toFixed(6)}
                                  </span>
                                </div>
                              </div>}
                          </div>
                        </div>}
                    </div>;
          })}
              </div>
            </div>)}
      </div>
    </div>;
}